import type { ConversationFileDto, DocumentDto } from '@dbrain/shared';
import { Icons } from '@dbrain/ui';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocument } from '../../api/documents.js';
import { documentSize, KindBadge } from '../documents/DocumentParts.js';
import { formatSize } from './ChatParts.js';

/**
 * Files in a composer before they are sent, the same on the home page and in
 * a conversation (D-93): each is uploaded the moment it is attached and read
 * meanwhile, and its chip inside the box says where it is: Yükleniyor, then
 * Okunuyor, then Hazır · 8 sayfa (with a note when its text was recognized
 * from images). Content attached already is not added again; the chip says
 * so (D-94). Taking a chip out before sending deletes what this upload made.
 */
export interface ComposerFile {
  key: string;
  name: string;
  size: number;
  /** the stored file, once uploaded */
  file: ConversationFileDto | null;
  uploading: boolean;
  failed: boolean;
  /** the same content was attached before: this chip stands for that file */
  duplicate: boolean;
  /** this upload made the file, so taking the chip out deletes it */
  own: boolean;
}

export interface ComposerFilesApi {
  upload(file: File): Promise<ConversationFileDto>;
  remove(file: ConversationFileDto): Promise<unknown>;
}

/**
 * A file that is not on this computer: Microsoft 365 part 3's "Bu sohbet" (a OneDrive, SharePoint or Outlook file).
 * The server copies it like an upload; its chip is the same.
 */
export interface ComposerSource {
  name: string;
  size: number;
  fetch(): Promise<ConversationFileDto>;
}

let counter = 0;

export function useComposerFiles(api: ComposerFilesApi) {
  const [entries, setEntries] = useState<ComposerFile[]>([]);
  const [error, setError] = useState<unknown>(null);
  // uploads go one after another, so the home page's draft is the same for all of them
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const latest = useRef(api);
  useLayoutEffect(() => {
    latest.current = api;
  });
  // what each chip stands for, kept as the uploads end (sending reads it without waiting for a render)
  const stored = useRef(new Map<string, { file: ConversationFileDto; own: boolean }>());
  // chips taken out while still uploading: what they made is deleted when the upload ends
  const dropped = useRef(new Set<string>());

  /** one chip, its file stored after the ones before it (an upload, or a copy the server makes) */
  const enqueue = useCallback((name: string, size: number, store: () => Promise<ConversationFileDto>) => {
    counter += 1;
    const key = `f${counter}`;
    setEntries((list) => [...list, { key, name: name || 'dosya', size, file: null, uploading: true, failed: false, duplicate: false, own: false }]);
    queue.current = queue.current.then(async () => {
      try {
        const file = await store();
        if (dropped.current.has(key)) {
          if (!file.duplicate) await latest.current.remove(file).catch(() => undefined);
          return;
        }
        const earlier = [...stored.current.entries()].find(([, value]) => value.file.id === file.id)?.[0];
        if (earlier) {
          // the same content twice in this box: one chip, which says so
          setEntries((list) => list.filter((entry) => entry.key !== key).map((entry) => (entry.key === earlier ? { ...entry, duplicate: true } : entry)));
          return;
        }
        const own = file.duplicate !== true;
        stored.current.set(key, { file, own });
        setEntries((list) => list.map((entry) => (entry.key === key ? { ...entry, file, uploading: false, duplicate: !own, own } : entry)));
      } catch (caught) {
        setError(caught);
        setEntries((list) => list.map((entry) => (entry.key === key ? { ...entry, uploading: false, failed: true } : entry)));
      }
    });
  }, []);

  const add = useCallback(
    (files: File[]) => {
      for (const picked of files) enqueue(picked.name, picked.size, () => latest.current.upload(picked));
    },
    [enqueue],
  );

  /** Microsoft 365's "Bu sohbet": each picked file is copied by the server in turn, a chip each (part 3) */
  const addSource = useCallback(
    (sources: ComposerSource[]) => {
      for (const source of sources) enqueue(source.name, source.size, () => source.fetch());
    },
    [enqueue],
  );

  const remove = useCallback((key: string) => {
    const kept = stored.current.get(key);
    if (kept) {
      stored.current.delete(key);
      if (kept.own) void latest.current.remove(kept.file).catch(() => undefined);
    } else dropped.current.add(key);
    setEntries((list) => list.filter((entry) => entry.key !== key));
  }, []);

  /** after sending: the files belong to the message now */
  const clear = useCallback(() => {
    stored.current.clear();
    setEntries([]);
    setError(null);
  }, []);

  /** every upload started so far has ended */
  const settled = useCallback(() => queue.current.then(() => undefined), []);
  /** the files to send, each once */
  const ids = useCallback(() => [...new Set([...stored.current.values()].map((value) => value.file.id))], []);
  /** what was uploaded here and not sent: the home page takes it back when it goes away */
  const unsent = useCallback(() => [...stored.current.values()].filter((value) => value.own).map((value) => value.file), []);

  return { entries, ids, add, addSource, remove, clear, settled, unsent, error, busy: entries.some((entry) => entry.uploading) };
}

/** Takes back, when the box goes away unsent, what its uploads made (the home page). */
export function useDropOnLeave(unsent: () => ConversationFileDto[], remove: (file: ConversationFileDto) => Promise<unknown>) {
  const latest = useRef({ unsent, remove });
  useLayoutEffect(() => {
    latest.current = { unsent, remove };
  });
  useEffect(
    () => () => {
      for (const file of latest.current.unsent()) void latest.current.remove(file).catch(() => undefined);
    },
    [],
  );
}

function FileState({ entry, doc }: { entry: ComposerFile; doc: DocumentDto | undefined }) {
  const { t } = useTranslation();
  if (entry.failed) return <span className="composer-file__state composer-file__state--failed">{t('attach.failed')}</span>;
  if (entry.uploading) {
    return (
      <span className="composer-file__state composer-file__state--busy">
        <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
        {t('attach.uploading')}
      </span>
    );
  }
  // a file that is not a document (a ZIP, a script) is only stored
  if (!entry.file?.documentId) return <span className="composer-file__state">{formatSize(entry.size)}</span>;
  if (!doc || doc.status === 'queued' || doc.status === 'processing') {
    const progress = doc?.progress ? ` (${doc.progress.done}/${doc.progress.total})` : '';
    return (
      <span className="composer-file__state composer-file__state--busy">
        <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
        {`${t('attach.reading')}${progress}`}
      </span>
    );
  }
  if (doc.status === 'failed') {
    const reason = t(`documents.errors.${doc.errorCode ?? 'processing_failed'}`, { defaultValue: t('documents.errors.processing_failed') });
    return <span className="composer-file__state composer-file__state--failed">{t('attach.unreadable', { reason })}</span>;
  }
  const size = documentSize(doc, t);
  return (
    <span className="composer-file__state composer-file__state--ready">
      <Icons.done size={14} />
      {size ? t('attach.readyWith', { size }) : t('attach.ready')}
      {doc.ocr ? <span className="composer-file__note"> · {t('attach.ocr')}</span> : null}
    </span>
  );
}

function ComposerFileChip({ entry, onRemove }: { entry: ComposerFile; onRemove: (key: string) => void }) {
  const { t } = useTranslation();
  // the document is read on the server meanwhile; its state is asked for until it is ready
  const doc = useDocument(entry.file?.documentId ?? null).data;
  const status = entry.failed ? 'failed' : entry.uploading ? 'uploading' : !entry.file?.documentId ? 'file' : (doc?.status ?? 'queued');
  return (
    <li className="chip attachment-chip composer-file" data-testid="attachment-chip" data-status={status} data-duplicate={entry.duplicate ? 'true' : undefined}>
      <KindBadge kind={doc?.kind} name={entry.name} />
      <span className="composer-file__name">{entry.name}</span>
      <FileState entry={entry} doc={doc} />
      {entry.duplicate ? (
        <span className="composer-file__note" role="status" data-testid="attachment-duplicate">
          {t('attach.duplicate')}
        </span>
      ) : null}
      <button type="button" className="ui-icon-button ui-icon-button--sm" aria-label={t('attach.remove', { name: entry.name })} title={t('attach.remove', { name: entry.name })} onClick={() => onRemove(entry.key)} data-testid="attachment-remove">
        <Icons.close size={14} />
      </button>
    </li>
  );
}

/** The chips inside the box. */
export function ComposerFiles({ entries, onRemove }: { entries: ComposerFile[]; onRemove: (key: string) => void }) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;
  return (
    <ul className="chips composer-files" aria-label={t('attach.label')} data-testid="composer-files">
      {entries.map((entry) => (
        <ComposerFileChip key={entry.key} entry={entry} onRemove={onRemove} />
      ))}
    </ul>
  );
}
